import React, { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';

import { useLocalSearchParams, useRouter } from 'expo-router';
import {
  MenuNavigationTabBorderBottom,
  MenuNavigationTabLink,
  MenuNavigationTabText,
} from 'features/menu/components/menu-navigation-tabs/styled';
import { MenuSectionTabsSkeleton } from 'features/menu/pages/menu-object-page/menu-section/menu-section-tabs-skeleton';
import { useMenuSectionTabs } from 'features/menu/pages/menu-object-page/menu-section/use-menu-section-tabs';
import { logButtonClick } from 'features/navigation/utils/analytics';
import { LayoutChangeEvent, Platform, ScrollView } from 'react-native';

import { IMainMenuView } from '@rbi-ctg/menu';
import { DraggableScrollView } from 'components/draggable-scrollview';
import { CenteredRowContainer } from 'components/layout/centered-row-container';
import { IGetMenuSectionsQuery } from 'generated/sanity-graphql';
import { useIsDesktopBreakpoint } from 'hooks/use-media-query';
import { ClickEventComponentNames, CustomEventNames, EventTypes, logEvent } from 'state/amplitude';

interface IMenuSectionTabsProps {
  menuData: IGetMenuSectionsQuery['Menu'] | IMainMenuView | null | undefined;
  menuDataLoading: boolean;
  headerBackgroundColorOverride?: string | null;
}

export const MenuSectionTabs: React.FC<IMenuSectionTabsProps> = ({
  menuData,
  menuDataLoading,
  headerBackgroundColorOverride,
}) => {
  const { menuSectionTabs } = useMenuSectionTabs({
    menuData,
  });

  const router = useRouter();

  const { id } = useLocalSearchParams<{ id: string }>();

  // Horizontal Scrolling feature
  const scrollViewRef = useRef<ScrollView | null>(null);
  const activeTabIndex = useMemo(() => {
    return menuSectionTabs.findIndex(tab => {
      const bool = tab?.to.includes(id);

      return bool;
    });
  }, [id, menuSectionTabs]);
  const [coordinates, setCoordinates] = useState<{ [key: number]: number } | null>(null);
  const onItemLayout = (event: LayoutChangeEvent, index: number) => {
    const { x } = event.nativeEvent.layout;
    setCoordinates(prev => ({ ...prev, [index]: x }));
  };
  useLayoutEffect(() => {
    if (coordinates && coordinates[activeTabIndex] !== undefined) {
      scrollViewRef.current?.scrollTo({ x: coordinates[activeTabIndex], animated: true });
    }
  }, [activeTabIndex, coordinates]);
  // --- end of horizontal scrolling feature

  const logTabSelected = useCallback((entry: string, tabItemId: string): void => {
    logEvent(CustomEventNames.TABBED_HEADER, EventTypes.Navigation, {
      'Selected Tab': entry,
    });

    logButtonClick({
      attributes: {
        component: ClickEventComponentNames.PRODUCT_SECTION_TABS,
        componentId: tabItemId,
        name: entry,
      },
    });
  }, []);

  const handleMenuTabPress = (label: string, tabItemId: string) => {
    logTabSelected(label, tabItemId);
    router.setParams({ id: tabItemId });
  };

  const isDesktop = useIsDesktopBreakpoint();
  const isDesktopWeb = isDesktop && Platform.OS === 'web';
  const ScrollViewComponent = isDesktopWeb ? DraggableScrollView : ScrollView;

  if (menuDataLoading) {
    return <MenuSectionTabsSkeleton testID="tabbed-menu-header-skeleton" />;
  }

  return (
    <CenteredRowContainer style={{ backgroundColor: headerBackgroundColorOverride || undefined }}>
      <ScrollViewComponent
        ref={scrollViewRef}
        horizontal
        showsHorizontalScrollIndicator={false}
        testID="tabbed-menu-header"
      >
        {menuSectionTabs.map(({ label, tabItemId }, index) => (
          <MenuNavigationTabLink
            key={label}
            testID={`tabbed-menu-${label}`}
            active={index === activeTabIndex}
            onPress={() => handleMenuTabPress(label, tabItemId)}
            onLayout={event => onItemLayout(event, index)}
            accessible
            focusable
            accessibilityRole="link"
            {...(isDesktopWeb
              ? {
                  // Desktop Web specific support for key presses like "Enter" or "Space"
                  tabIndex: 0,
                  onKeyDown: (event: React.KeyboardEvent<HTMLInputElement>) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      handleMenuTabPress(label, tabItemId);
                    }
                  },
                }
              : {})}
          >
            <MenuNavigationTabText active={index === activeTabIndex}>{label}</MenuNavigationTabText>
          </MenuNavigationTabLink>
        ))}
      </ScrollViewComponent>
      {isDesktopWeb && <MenuNavigationTabBorderBottom />}
    </CenteredRowContainer>
  );
};
